import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@/components/ui/Icons';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import './ImageLightbox.css';

interface ImageLightboxProps {
  src: string;
  alt?: string;
  open: boolean;
  onClose: () => void;
}

export function ImageLightbox({ src, alt = '', open, onClose }: ImageLightboxProps) {
  const { shouldRender, backdropClasses, contentClasses, requestClose } = useOverlayAnimation(open, onClose);
  const mousedownOnBackdropRef = useRef(false);

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [shouldRender, requestClose]);

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`lightbox-overlay ${backdropClasses}`}
      onMouseDown={(e) => {
        mousedownOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (mousedownOnBackdropRef.current && e.target === e.currentTarget) {
          requestClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="lightbox__close"
        onClick={requestClose}
        aria-label="Закрыть"
        title="Закрыть"
      >
        <CloseIcon size={24} />
      </button>
      <OverlayIntrinsicSize variant="centered">
        <div className={`lightbox__content ${contentClasses}`} onClick={(e) => e.stopPropagation()}>
          <img src={src} alt={alt} className="lightbox__image" />
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
